import React, { useContext, useEffect, useState } from 'react';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import { WebsiteLink } from 'src/components/common/website-link';
import mq from 'src/styling/media-queries';
import { flex } from 'src/styling/constants';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import {
  backgroundAndTextColorForRibbon,
  createAnimatedStyles,
  isPromoEnabledOnPage,
} from 'src/components/layout/redesign/header-shared/promo-helper';
import { useRibbonEffects } from 'src/custom-hooks/use-ribbon-effect';
import Markdown from 'src/components/common/markdown';
import { markdownBulletStyle } from 'src/helpers/styling';
import TimeCircle from 'src/components/icons/time-circle';
import { TranslatedStaticText } from 'src/translators/static-text-translator';
import { mqDesktopHeader } from 'src/components/layout/redesign/header-shared/header-styling';
import { getSessionStorageItem, setSessionStorageItem } from 'src/helpers/session-storage-helper';

const createComponentStyling = (
  styling: Styling,
  isMobile: boolean,
  color: string,
  isIconVisible: boolean
) =>
  ({
    wrapper: [
      flex.columnCentered,
      mq({
        width: '100%',
        padding: '1rem 0',
      }),
    ],
    container: [
      flex.row,
      {
        width: '100%',
        maxWidth: '92.25rem',
        justifyContent: isIconVisible ? 'space-between' : 'center',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '0.5rem',
      },
      isMobile
        ? { padding: '0 1.25rem' }
        : mqDesktopHeader({
            padding: ['0 1.25rem', '0 1.25rem', '0 3.75rem'],
          }),
    ],
    textWrapper: [
      styling.fonts.regular,
      {
        display: 'flex',
        alignItems: ['flex-start', 'center'],
        flexDirection: 'row',
        flexWrap: 'wrap',
        maxWidth: '100%',
        textWrap: 'wrap',
        fontSize: '1rem',
        lineHeight: '125%',
      },
    ],
    link: {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    expandButton: {
      background: 'unset',
      border: 'unset',
      padding: '0.5rem',
      margin: '-0.5rem',
    },
    closeWrapper: [
      styling.fonts.regular,
      {
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'flex-end',
        fontSize: '1rem',
        textDecoration: 'underline',
        lineHeight: '125%',
        height: '100%',
        cursor: 'pointer',
        minWidth: '2rem',
      },
    ],
    markdown: mq([
      {
        'h1, h2, p': {
          color: color,
        },
        'p': {
          margin: '0',
        },
        'a': {
          color: `${color} !important`,
          borderBottom: 'none !important',
        },
      },
    ]),
  }) satisfies Record<string, CSSInterpolation>;

export type PromoRibbonProps = {
  isMobileNavOpen?: boolean;
  isCollapsed?: boolean;
  isMobile?: boolean;
  onPointerOver?: () => void;
  promoRibbon: Queries.HeaderPromoRibbon;
};

const SESSION_STORAGE_KEY = 'promo-ribbon';

const PromoRibbon = ({
  isMobileNavOpen,
  onPointerOver,
  isCollapsed,
  promoRibbon,
  isMobile,
}: PromoRibbonProps) => {
  const { styling, language, buildType, basePath, pageLevel, isGlobalPage, siteId } =
    useContext(SiteContext);
  const {
    componentHeight,
    updateComponentHeight,
    shouldHide,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  } = useRibbonEffects(0);
  const [ribbonState, setRibbonState] = useState<null | string>(
    getSessionStorageItem(SESSION_STORAGE_KEY)
  );

  useEffect(() => {
    updateComponentHeight();
  }, [promoRibbon, updateComponentHeight]);

  useEffect(() => {
    if (ribbonState === null) {
      setSessionStorageItem(SESSION_STORAGE_KEY, 'visible');
      setRibbonState('visible');
    }
  }, [ribbonState]);

  if (
    !isPromoEnabledOnPage(
      promoRibbon,
      basePath,
      language,
      buildType,
      isGlobalPage ? undefined : siteId
    ) ||
    !promoRibbon?.text
  ) {
    return null;
  }

  const isIconVisible = !!promoRibbon?.enabledCrossIcon;
  const backgroundAndTextColorStyle = backgroundAndTextColorForRibbon(styling, promoRibbon.color);
  const componentStyling = createComponentStyling(
    styling,
    !!isMobile,
    backgroundAndTextColorStyle.color,
    isIconVisible
  );
  const shouldHideComponent =
    shouldHide || isCollapsed || ribbonState === null || ribbonState === 'close';
  const animatedStyles = createAnimatedStyles(
    componentHeight,
    shouldHideComponent,
    isMobileNavOpen
  );

  const handleClick = () =>
    amplitudeTrackEvent(`Click to Promo Ribbon link`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
    });

  const onClick = () => {
    setRibbonState('close');
    setSessionStorageItem(SESSION_STORAGE_KEY, 'close');
  };

  return (
    <div
      css={[
        animatedStyles.openCloseWrapper,
        {
          ...backgroundAndTextColorStyle,
        },
      ]}
      onPointerOver={onPointerOver}
    >
      <div
        css={componentStyling.wrapper}
        ref={componentRef}
        data-testid={`promo-ribbon-${componentHeight}`}
      >
        <div css={componentStyling.container} ref={textAndChevronContainerRef}>
          <div css={componentStyling.textWrapper} ref={textRef}>
            {promoRibbon.link ? (
              <WebsiteLink
                to={promoRibbon.link}
                css={componentStyling.link}
                handleClick={handleClick}
              >
                <Markdown
                  markdownText={promoRibbon.text}
                  cssOverride={[
                    componentStyling.markdown,
                    markdownBulletStyle(styling, backgroundAndTextColorStyle.color),
                  ]}
                />
              </WebsiteLink>
            ) : (
              <Markdown
                markdownText={promoRibbon.text}
                cssOverride={[
                  componentStyling.markdown,
                  markdownBulletStyle(styling, backgroundAndTextColorStyle.color),
                ]}
              />
            )}
          </div>
          {isIconVisible && (
            <div
              ref={chevronRef}
              tabIndex={0}
              css={componentStyling.closeWrapper}
              onClick={onClick}
            >
              {!isMobile && (
                <span css={{ marginRight: '0.62rem', color: backgroundAndTextColorStyle.color }}>
                  <TranslatedStaticText lookupKey="remove" />
                </span>
              )}
              <TimeCircle fill={backgroundAndTextColorStyle.color} size="1rem" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoRibbon;
